import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { 
  Calendar, 
  Clock, 
  Check, 
  ChevronDown, 
  ChevronUp,
  ArrowRight, 
  Users, 
  Lock, 
  ShieldCheck, 
  Award, 
  BookOpen,
  Volume2,
  CheckCircle2,
  X,
  AlertTriangle,
  HelpCircle,
  TrendingUp,
  MessageCircle,
  Map,
  Sparkles
} from "lucide-react";

// ==========================================
// 1. CONFIG BLOCK (Editable)
// ==========================================
const CONFIG = {
  BRAND_NAME: "Confident Parenting Hub",
  EXPERT_NAME: "Dr. Anuradha H S",
  EXPERT_TITLE: "Senior Pediatrician | Pediatric Nutritionist | Parenting Expert",
  MASTERCLASS_TITLE: "Starting Solids Made Simple",
  MASTERCLASS_PROMISE: "Know exactly what to feed your baby from 6–12 months — and how to move from first foods to family foods with confidence.",
  FORMAT: "Live Online Masterclass",
  LANGUAGE: "Simple English",
  DATE_1: "30 October 2026",
  DATE_2: "31 October 2026",
  TIME: "3:00 PM – 4:00 PM IST",
  SESSION_1_TITLE: "Starting Solids — 6–8 Months",
  SESSION_2_TITLE: "Moving Forward — 9–12 Months",
  REGULAR_PRICE: "₹4,999",
  OFFER_PRICE: "₹1,799",
  OFFER_LABEL: "Special Offer",
  CHECKOUT_URL: "https://learn.dranuradha.com/web/checkout/6ab9441cb2c414ab598c78bd?purchaseNow=true",
  EXPERT_PHOTO_URL: "https://tagmango.com/assets/036-8156296b6eb307cffcd466f4c01bd263.jpg",
  EXPERT_PHOTO_URL_2: "https://tagmango.com/assets/411-34213897069ca848b0b6ff21a47d7ef7.jpg",
  LOGO_URL: "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/e2/2c/d9/e22cd974-f76b-5a8d-3e86-d423ab50e2e6/Placeholder.mill/400x400bb-75.jpg",
  CONTACT_EMAIL: "support@dranuradha.com",
  TESTIMONIALS: [
    {
      name: "Meera Sen",
      baby_age: "7-Month-Old Baby",
      quote: "Session 1 was a game-changer! Learning about first foods, readiness signs, and how to build iron-dense bowls for my 7-month-old gave me absolute pediatric-backed confidence. Highly recommended!"
    },
    {
      name: "Rohan Patel",
      baby_age: "10-Month-Old Baby",
      quote: "We were stuck on purees and super anxious. Session 2's family food transition roadmap worked wonders for us."
    },
    {
      name: "Anjali Sharma",
      baby_age: "6-Month-Old Baby",
      quote: "No complicated nutrition terms, just pure pediatrician-backed clarity on what to cook and how to start safely."
    }
  ]
};

// ==========================================
// 2. ANALYTICS (Stub)
// ==========================================
const trackEvent = (name) => {
  console.log(`[Mango AI Analytics] Captured Event: ${name}`);
  if (window.posthog) {
    window.posthog.capture(name);
  }
};

// Confident Parenting Hub Logo Component
const ConfidentParentingHubLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src={CONFIG.LOGO_URL}
    alt="Confident Parenting Hub Logo"
    className={`${className} rounded-xl object-contain border border-[#5C0F68]/30 shadow-sm bg-[#2D0833]`}
    onError={(e) => {
      e.target.style.display = 'none';
    }}
  />
);

export default function App() {
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [faqOpenState, setFaqOpenState] = useState({});
  const heroRef = useRef(null);

  useEffect(() => {
    trackEvent("page_view");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.offsetTop + heroRef.current.offsetHeight;
        if (window.scrollY > heroBottom - 50) {
          setShowStickyBar(true);
        } else {
          setShowStickyBar(false);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleFaq = (index) => {
    setFaqOpenState((prev) => {
      const updated = { ...prev, [index]: !prev[index] };
      if (updated[index]) {
        trackEvent(`faq_open_${index}`);
      }
      return updated;
    });
  };

  const handleCtaClick = (sectionName) => {
    trackEvent(`${sectionName}_cta_click`);
    trackEvent("checkout_click");
    window.location.href = CONFIG.CHECKOUT_URL;
  };

  return (
    <div className="min-h-screen bg-[#2D0833] text-white font-sans antialiased selection:bg-[#689B3D]/30">
      
      {/* 1. STICKY TOP RIBBON */}
      <div 
        className="bg-gradient-to-r from-[#5C0F68] via-[#689B3D] to-[#2D0833] text-white py-2 px-4 text-center sticky top-0 z-40 text-xs sm:text-sm font-semibold flex flex-wrap items-center justify-center gap-x-3 gap-y-1 shadow-lg"
      >
        <span className="bg-white/20 px-2 py-0.5 rounded text-[10px] sm:text-xs tracking-wider uppercase font-bold text-white">
          {CONFIG.FORMAT}
        </span>
        <span className="opacity-90">{CONFIG.DATE_1} & {CONFIG.DATE_2} · {CONFIG.TIME}</span>
        <button 
          data-testid="ribbon-join-now-button"
          onClick={() => handleCtaClick("ribbon")}
          className="text-[11px] sm:text-xs text-white underline font-extrabold hover:text-[#689B3D] transition-colors ml-1"
        >
          Join Now →
        </button>
      </div>

      {/* NAVBAR */}
      <header className="border-b border-[#5C0F68]/30 bg-[#2D0833]/90 backdrop-blur-md sticky top-[36px] z-30 transition-all">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div data-testid="nav-brand-logo" className="flex items-center gap-3">
            <ConfidentParentingHubLogo className="w-10 h-10" />
            <div className="flex flex-col">
              <span className="font-extrabold text-white text-base leading-none tracking-tight">{CONFIG.BRAND_NAME}</span>
              <span className="text-[10px] text-[#689B3D] font-bold tracking-widest uppercase mt-1">{CONFIG.EXPERT_NAME}</span>
            </div>
          </div>
          
          <button 
            data-testid="nav-join-masterclass-button"
            onClick={() => handleCtaClick("header")}
            className="hidden sm:block bg-[#689B3D] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#689B3D]/90 transition transform hover:scale-105 active:scale-95 shadow-md"
          >
            Join Masterclass
          </button>
        </div>
      </header>

      <main>
        {/* 2. HERO SECTION */}
        <section 
          ref={heroRef}
          className="relative py-12 md:py-20 px-4 overflow-hidden bg-gradient-to-b from-[#2D0833] via-[#3d0b45] to-[#2D0833]"
        >
          {/* Subtle glowing elements */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#5C0F68]/20 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#689B3D]/10 rounded-full blur-[120px] pointer-events-none"></div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
            
            {/* HERO LEFT COLUMN */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="inline-block bg-[#5C0F68] border border-[#5C0F68]/40 text-white text-[11px] font-extrabold tracking-widest uppercase px-3 py-1 rounded-full">
                LIVE ONLINE MASTERCLASS · 6–12 MONTHS
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
                {CONFIG.MASTERCLASS_TITLE}
              </h1>

              <p className="text-base sm:text-lg text-white/90 font-medium border-l-4 border-[#689B3D] pl-4">
                {CONFIG.MASTERCLASS_PROMISE}
              </p>

              <p className="text-sm sm:text-base text-[#C1BEB7] leading-relaxed">
                Stop second-guessing every meal. Learn a simple, science-backed feeding roadmap from a pediatrician.
              </p>

              {/* Two Sessions Display */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-[#2D0833]/80 border border-[#5C0F68]/40 p-4 rounded-xl shadow-lg relative overflow-hidden group">
                  <div className="absolute top-0 left-0 w-1 h-full bg-[#689B3D]"></div>
                  <span className="text-[10px] font-bold text-[#689B3D] tracking-widest uppercase block mb-1">SESSION 1 · 6–8 MONTHS</span>
                  <h3 className="font-bold text-white text-base mb-1">{CONFIG.SESSION_1_TITLE}</h3>
                  <p className="text-xs text-[#C1BEB7] flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-[#689B3D]" /> {CONFIG.DATE_1}
                  </p>
                  <p className="text-xs text-[#C1BEB7] flex items-center gap-1 mt-0.5">
                    <Clock className="w-3.5 h-3.5 text-[#689B3D]" /> {CONFIG.TIME}
                  </p>
                </div>

                <div className="bg-[#2D0833]/80 border border-[#5C0F68]/40 p-4 rounded-xl shadow-lg relative overflow-hidden group">
                  <div className="absolute top-0 left-0 w-1 h-full bg-[#5C0F68]"></div>
                  <span className="text-[10px] font-bold text-[#689B3D] tracking-widest uppercase block mb-1">SESSION 2 · 9–12 MONTHS</span>
                  <h3 className="font-bold text-white text-base mb-1">{CONFIG.SESSION_2_TITLE}</h3>
                  <p className="text-xs text-[#C1BEB7] flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-[#5C0F68]" /> {CONFIG.DATE_2}
                  </p>
                  <p className="text-xs text-[#C1BEB7] flex items-center gap-1 mt-0.5">
                    <Clock className="w-3.5 h-3.5 text-[#5C0F68]" /> {CONFIG.TIME}
                  </p>
                </div>
              </div>

              {/* Price & Primary CTA */}
              <div className="bg-[#2D0833]/60 border border-[#5C0F68]/20 p-5 rounded-2xl space-y-4 shadow-xl">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-3xl sm:text-4xl font-black text-white tracking-tight">{CONFIG.OFFER_PRICE}</span>
                  <span className="text-sm font-bold text-[#689B3D] bg-[#689B3D]/10 border border-[#689B3D]/20 px-2 py-0.5 rounded">
                    2 LIVE SESSIONS · 6–12 MONTHS
                  </span>
                  <span className="text-white/60 text-base sm:text-lg line-through block mt-1">Regular {CONFIG.REGULAR_PRICE}</span>
                </div>

                <button 
                  data-testid="hero-join-masterclass-button"
                  onClick={() => handleCtaClick("hero")}
                  className="w-full bg-[#689B3D] text-white font-extrabold text-base py-4 px-6 rounded-xl hover:bg-[#689B3D]/90 transition duration-300 transform hover:scale-[1.01] active:scale-[0.99] shadow-lg flex items-center justify-center gap-2 tracking-wide"
                >
                  JOIN THE MASTERCLASS <ArrowRight className="w-5 h-5" />
                </button>

                <p className="text-[11px] sm:text-xs text-center text-[#C1BEB7] font-semibold">
                  2 live sessions · 6–12 months · {CONFIG.LANGUAGE}
                </p>
              </div>

            </div>

            {/* HERO RIGHT COLUMN (Dr. Anuradha Portrait) */}
            <div className="lg:col-span-5 relative flex justify-center w-full px-4 sm:px-0 mt-8 lg:mt-0">
              <div className="relative w-full max-w-[280px] sm:max-w-[320px] lg:max-w-none aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-2 border-[#5C0F68]/30 bg-black/40 group">
                <img 
                  src={CONFIG.EXPERT_PHOTO_URL} 
                  alt={CONFIG.EXPERT_NAME} 
                  className="w-full h-full object-cover object-top z-0 transition duration-500 group-hover:scale-[1.02] filter contrast-[1.03]" 
                  loading="eager"
                />
                
                {/* Bottom label overlay */}
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 z-20 bg-[#2D0833]/90 backdrop-blur-sm p-2.5 sm:p-3.5 rounded-xl border border-[#5C0F68]/30">
                  <h4 className="font-extrabold text-xs sm:text-sm text-white">{CONFIG.EXPERT_NAME}</h4>
                  <p className="text-[8px] sm:text-[10px] text-[#C1BEB7] font-bold tracking-wide uppercase mt-0.5 sm:mt-1 leading-tight">{CONFIG.EXPERT_TITLE}</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* 3. PROBLEM SECTION */}
        <section className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-4xl mx-auto text-center space-y-10">
            <div className="space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">THE REAL STRUGGLE</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Starting solids sounds simple. <br className="hidden sm:inline" />
                <span className="text-[#689B3D]">Until you actually have to do it.</span>
              </h2>
            </div>

            {/* Parent Conflicting Thoughts */}
            <div className="bg-[#2D0833]/40 border border-[#5C0F68]/30 p-6 rounded-2xl relative overflow-hidden shadow-lg max-w-2xl mx-auto">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#5C0F68]/10 rounded-full blur-2xl"></div>
              <div className="space-y-4 text-left italic text-white/90 text-sm sm:text-base leading-relaxed">
                <p className="flex items-start gap-2">
                  <span className="text-[#689B3D] font-extrabold text-lg">“</span>
                  One person says start with purees. Another says give finger foods.
                </p>
                <p className="flex items-start gap-2 border-t border-[#5C0F68]/20 pt-3">
                  <span className="text-[#689B3D] font-extrabold text-lg">“</span>
                  Google gives you 20 different feeding schedules. And everyone has an opinion.
                </p>
              </div>
            </div>

            {/* Parent Question Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-left pt-4">
              {[
                "When should I start?",
                "What should I give first?",
                "How much should my baby eat?",
                "Is fruit puree enough?",
                "When should I introduce eggs?",
                "When do I increase texture?",
                "What if my baby refuses food?",
                "How do I move from baby food to family food?",
                "Am I giving my baby enough nutrition?"
              ].map((question, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#2D0833]/80 border border-[#5C0F68]/20 p-4 rounded-xl hover:border-[#689B3D]/40 transition duration-300 flex items-start gap-2.5 shadow-sm"
                >
                  <AlertTriangle className="w-5 h-5 text-[#5C0F68] shrink-0 mt-0.5" />
                  <span className="text-sm font-semibold text-white/95">{question}</span>
                </div>
              ))}
            </div>

            {/* Strong Roadmap Transition */}
            <div className="pt-6 space-y-3">
              <p className="text-lg sm:text-xl font-bold text-[#C1BEB7]">
                You don't need more random advice.
              </p>
              <p className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                You need a <span className="text-[#689B3D] underline decoration-wavy underline-offset-8">clear roadmap.</span>
              </p>
            </div>

          </div>
        </section>

        {/* 4. THE BIG PROMISE */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#2D0833] via-[#350b40] to-[#2D0833]">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <div className="space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">THE BIG TRANSFORMATION</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                By the end of these 2 sessions, <br className="hidden sm:inline" />
                you will know what comes next.
              </h2>
              <p className="text-sm sm:text-base text-[#C1BEB7] max-w-2xl mx-auto leading-relaxed">
                Instead of trying to figure out every meal separately, you'll understand the feeding journey from 6 to 12 months.
              </p>
            </div>

            {/* Benefit Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {[
                { title: "Know what foods to offer", desc: "No more guesswork. Learn exactly what nutrition-packed ingredients belong in your baby's bowl." },
                { title: "Understand how much & how often", desc: "Learn to recognize hunger cues, set meal timings, and understand healthy portion expectations." },
                { title: "Know how to progress textures", desc: "Smooth purees are only the start. Confidently transition from mashed to chunky textures safely." },
                { title: "Understand how to introduce new foods", desc: "Introduce allergenic foods and complex textures systematically with clinical guidance." },
                { title: "Learn how to build balanced meals", desc: "Combine carbohydrates, healthy fats, and iron-rich proteins into perfect combinations." },
                { title: "Know how to move towards family foods", desc: "Prepare a smooth transition to table food so they can eat what you eat by age one." }
              ].map((benefit, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#2D0833]/90 border border-[#5C0F68]/30 p-5 rounded-xl hover:border-[#689B3D]/30 transition-all shadow-md relative overflow-hidden"
                >
                  <div className="w-8 h-8 rounded-full bg-[#689B3D]/10 border border-[#689B3D]/30 flex items-center justify-center mb-4">
                    <Check className="w-5 h-5 text-[#689B3D]" />
                  </div>
                  <h3 className="font-extrabold text-white text-base mb-2">{benefit.title}</h3>
                  <p className="text-xs text-[#C1BEB7] leading-relaxed">{benefit.desc}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* 5. TWO-SESSION ROADMAP */}
        <section className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-5xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">CURRICULUM HIGHLIGHTS</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                One masterclass. <br className="hidden sm:inline" />
                Two stages of your baby's feeding journey.
              </h2>
              <p className="text-sm sm:text-base text-[#C1BEB7] max-w-2xl mx-auto leading-relaxed">
                The sessions are logically divided to cover starting solids safely and successfully transitioning to family meals.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4">
              
              {/* SESSION 1 CARD */}
              <div className="bg-[#2D0833] border-2 border-[#689B3D]/30 rounded-2xl shadow-xl overflow-hidden flex flex-col justify-between">
                <div>
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-[#689B3D]/20 to-[#689B3D]/5 p-6 border-b border-[#689B3D]/20">
                    <div className="flex justify-between items-start gap-4">
                      <span className="bg-[#689B3D] text-white text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded">
                        STAGE 1 · LIVE
                      </span>
                      <div className="text-right text-xs text-[#C1BEB7] font-semibold space-y-0.5">
                        <p className="text-white font-bold">{CONFIG.DATE_1}</p>
                        <p>{CONFIG.TIME}</p>
                      </div>
                    </div>
                    <p className="text-xs font-bold text-[#689B3D] uppercase tracking-wider mt-4">6–8 MONTHS</p>
                    <h3 className="text-xl sm:text-2xl font-black text-white mt-1">“{CONFIG.SESSION_1_TITLE}”</h3>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 space-y-4">
                    <h4 className="font-bold text-xs text-[#C1BEB7] uppercase tracking-wider">What We Will Cover:</h4>
                    <ul className="space-y-3">
                      {[
                        "Is my baby ready for solids? Essential readiness signs checklist.",
                        "When and how to start your very first feeding session.",
                        "What foods to offer first to support digestive health.",
                        "First meals, nutritional balance, and introducing food variety.",
                        "How to build balanced infant bowls with iron-rich foods.",
                        "Safely navigating purees, mashed foods, and BLW-style finger foods.",
                        "Introducing allergenic foods carefully and tracking responses.",
                        "Quantity guide: exactly how much and how often to feed.",
                        "Gagging vs Choking myths, reflexes, and feeding safety protocols."
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-white/90">
                          <CheckCircle2 className="w-4 h-4 text-[#689B3D] shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="p-6 bg-stone-900/10 border-t border-[#5C0F68]/20">
                  <p className="text-xs sm:text-sm text-[#C1BEB7] font-medium italic text-center">
                    “Perfect if your baby is about to start solids or is in the early months of solids.”
                  </p>
                </div>
              </div>

              {/* SESSION 2 CARD */}
              <div className="bg-[#2D0833] border-2 border-[#5C0F68]/30 rounded-2xl shadow-xl overflow-hidden flex flex-col justify-between">
                <div>
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-[#5C0F68]/20 to-[#5C0F68]/5 p-6 border-b border-[#5C0F68]/20">
                    <div className="flex justify-between items-start gap-4">
                      <span className="bg-[#5C0F68] text-white text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded">
                        STAGE 2 · LIVE
                      </span>
                      <div className="text-right text-xs text-[#C1BEB7] font-semibold space-y-0.5">
                        <p className="text-white font-bold">{CONFIG.DATE_2}</p>
                        <p>{CONFIG.TIME}</p>
                      </div>
                    </div>
                    <p className="text-xs font-bold text-[#689B3D] uppercase tracking-wider mt-4">9–12 MONTHS</p>
                    <h3 className="text-xl sm:text-2xl font-black text-white mt-1">“{CONFIG.SESSION_2_TITLE}”</h3>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 space-y-4">
                    <h4 className="font-bold text-xs text-[#C1BEB7] uppercase tracking-wider">What We Will Cover:</h4>
                    <ul className="space-y-3">
                      {[
                        "How to successfully move beyond smooth and watery purees.",
                        "Safely increasing food texture, chunkiness, and ingredients.",
                        "Navigating finger foods, self-feeding milestones, and fine motor skills.",
                        "Building complete balanced meals with proteins, fats, and grains.",
                        "Structuring daily meal schedules: breakfast, lunch, snack, dinner.",
                        "Transitioning to family foods: what stays safe, what changes.",
                        "Under-the-hood solutions for food refusal and throwing food.",
                        "Appetite fluctuations: understanding and managing normal drops.",
                        "Establishing integrated family meal timings by age 1."
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-white/90">
                          <CheckCircle2 className="w-4 h-4 text-[#689B3D] shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="p-6 bg-stone-900/10 border-t border-[#5C0F68]/20">
                  <p className="text-xs sm:text-sm text-[#C1BEB7] font-medium italic text-center">
                    “Perfect if your baby is already eating solids and you're wondering what to do next.”
                  </p>
                </div>
              </div>

            </div>

          </div>
        </section>

        {/* 6. VISUAL FEEDING ROADMAP */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#2D0833] via-[#330c3d] to-[#2D0833]">
          <div className="max-w-4xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">FEEDING TIMELINE</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Your Baby's Feeding Journey At A Glance
              </h2>
            </div>

            {/* Journey Timeline Elements */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 relative">
              {/* Horizontal line for desktop connection */}
              <div className="hidden md:block absolute top-[44px] left-10 right-10 h-0.5 bg-[#5C0F68]/30 z-0"></div>

              {[
                { stage: "6 MONTHS", title: "First Foods", desc: "Single ingredient smooth purees, mashed foods, and basic solid checks." },
                { stage: "6–8 MONTHS", title: "Texture & Variety", desc: "Soft lumps, thick purees, allergen checks, and multiple flavors." },
                { stage: "9–10 MONTHS", title: "Finger Foods", desc: "Chunky textures, self-feeding practice, grasping food, and three mini-meals." },
                { stage: "11–12 MONTHS", title: "Family Meals", desc: "Soft family foods, sharing spices, and complete dining integration." }
              ].map((step, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#2D0833]/90 border border-[#5C0F68]/40 p-5 rounded-xl relative z-10 hover:border-[#689B3D]/50 transition duration-300 shadow-lg text-center md:text-left flex flex-col items-center md:items-start"
                >
                  <span className="text-xs font-black text-[#689B3D] bg-[#689B3D]/10 px-2.5 py-1 rounded-full mb-3 inline-block">
                    {step.stage}
                  </span>
                  <h4 className="font-extrabold text-white text-base mb-1">{step.title}</h4>
                  <p className="text-xs text-[#C1BEB7] leading-relaxed text-center md:text-left">{step.desc}</p>
                </div>
              ))}
            </div>

            <p className="text-xs sm:text-sm text-[#C1BEB7] italic text-center max-w-lg mx-auto">
              “Every baby develops at their own pace. This roadmap gives you the principles and practical next steps.”
            </p>

          </div>
        </section>

        {/* 7. WHAT YOU WILL LEARN */}
        <section className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-5xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">COMPREHENSIVE LEARNING</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Inside the masterclass
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "STARTING SOLIDS", desc: "Know when and how to begin properly with confidence." },
                { title: "FOOD CHOICES", desc: "Understand what foods to offer and how to build rich meal variety." },
                { title: "TEXTURE", desc: "Know when and how to move beyond smooth purees safely." },
                { title: "NUTRITION", desc: "Build balanced meals packed with essential pediatric nutrients." },
                { title: "FOOD INTRODUCTION", desc: "Understand how to introduce new ingredients and allergens." },
                { title: "PORTIONS", desc: "Learn realistic expectations around quantity and weekly frequency." },
                { title: "FINGER FOODS", desc: "Understand how safe self-feeding fits into the journey." },
                { title: "FAMILY FOOD", desc: "Know how and when to move towards sharing family meals." },
                { title: "FEEDING CHALLENGES", desc: "Know what to do when baby refuses food or eats very little." }
              ].map((item, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#2D0833]/50 border border-[#5C0F68]/20 p-5 rounded-xl flex gap-3.5"
                >
                  <div className="shrink-0 w-6 h-6 rounded-full bg-[#5C0F68]/20 border border-[#5C0F68]/30 flex items-center justify-center text-xs text-[#689B3D] font-bold">
                    {idx + 1}
                  </div>
                  <div className="space-y-1 text-left">
                    <h4 className="font-extrabold text-sm sm:text-base text-white tracking-wide">{item.title}</h4>
                    <p className="text-xs text-[#C1BEB7] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* 8. WHO IS THIS FOR / NOT FOR */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#2D0833] via-[#300b3a] to-[#2D0833]">
          <div className="max-w-4xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">SUITABILITY CHECK</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                This masterclass is for you if...
              </h2>
            </div>

            {/* Target parent categories */}
            <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
              {[
                "Your baby is about to start solids",
                "Your baby is 6–8 months",
                "Your baby is 9–12 months",
                "You're confused about what to feed",
                "Your baby is still mostly eating purees",
                "You want a clear feeding roadmap"
              ].map((chip, idx) => (
                <span 
                  key={idx} 
                  className="bg-[#2D0833] border border-[#5C0F68] text-white/95 text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-lg hover:border-[#689B3D]/50 transition shadow-sm"
                >
                  {chip}
                </span>
              ))}
            </div>

            <p className="text-xs sm:text-sm text-center text-[#C1BEB7] max-w-lg mx-auto font-medium">
              “Especially useful for first-time parents who want practical expert guidance instead of piecing together conflicting advice from Instagram, Google and family.”
            </p>

            {/* WHO THIS IS NOT FOR */}
            <div className="bg-[#2D0833]/80 border border-[#5C0F68]/30 rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto text-left relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#5C0F68]"></div>
              <h3 className="font-extrabold text-white text-base mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#5C0F68]" /> Clinical Care Disclaimer
              </h3>
              <div className="space-y-3 text-xs sm:text-sm text-[#C1BEB7] leading-relaxed">
                <p>
                  This is an educational masterclass, not an individual medical consultation.
                </p>
                <p>
                  If your baby has a specific medical condition, significant feeding difficulty, swallowing concerns, poor growth or another clinical concern, personalised assessment with a doctor may be needed.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* 9. WHY DR. ANURADHA (TRUST SECTION) */}
        <section id="meet-instructor-section" data-testid="meet-instructor-section" className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            
            <div className="space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">EXPERT CREDIBILITY</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Learn from a pediatrician who works with <br className="hidden sm:inline" />
                babies, children and parents every day.
              </h2>
            </div>

            <div className="bg-[#2D0833] border border-[#5C0F68]/30 rounded-2xl shadow-xl overflow-hidden grid grid-cols-1 md:grid-cols-12 text-left">
              
              <div className="md:col-span-5 flex justify-center items-center bg-[#2D0833] p-6 border-b md:border-b-0 md:border-r border-[#5C0F68]/20">
                <div className="relative w-full max-w-[260px] sm:max-w-[280px] aspect-[3/4] rounded-2xl overflow-hidden shadow-xl border-2 border-[#5C0F68]/30">
                  <img 
                    src={CONFIG.EXPERT_PHOTO_URL_2} 
                    alt={CONFIG.EXPERT_NAME} 
                    className="w-full h-full object-cover object-top" 
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="md:col-span-7 p-6 sm:p-8 space-y-5 flex flex-col justify-center">
                <div>
                  <h3 className="text-2xl font-black text-white">{CONFIG.EXPERT_NAME}</h3>
                  <p className="text-xs sm:text-sm text-[#689B3D] font-extrabold uppercase tracking-widest mt-1">
                    {CONFIG.EXPERT_TITLE}
                  </p>
                  <p className="text-xs text-[#C1BEB7] font-semibold uppercase tracking-wider mt-0.5">
                    Founder, {CONFIG.BRAND_NAME}
                  </p>
                </div>

                {/* Key stats row */}
                <div className="grid grid-cols-2 gap-4 border-t border-b border-[#5C0F68]/20 py-4">
                  <div>
                    <h4 className="text-2xl font-black text-white">30+ Years</h4>
                    <p className="text-[10px] text-[#C1BEB7] font-semibold uppercase tracking-wider">Clinical Experience</p>
                  </div>
                  <div>
                    <h4 className="text-2xl font-black text-white">250K+</h4>
                    <p className="text-[10px] text-[#C1BEB7] font-semibold uppercase tracking-wider">Families Served</p>
                  </div>
                </div>

                <p className="text-xs sm:text-sm text-[#C1BEB7] leading-relaxed">
                  Dr. Anuradha combines pediatric experience with practical parenting education to help parents understand what to do — without making feeding unnecessarily complicated.
                </p>
              </div>

            </div>

          </div>
        </section>

        {/* 10. WHY THIS MASTERCLASS IS DIFFERENT */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#2D0833] via-[#350b40] to-[#2D0833]">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            
            <div className="space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">WHAT MAKES US UNIQUE</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Not another list of baby-food recipes.
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
              {[
                { title: "SCIENCE-BACKED", desc: "Understand the biological principles behind what you are doing, based on modern WHO & pediatric clinical guidelines." },
                { title: "AGE-SPECIFIC", desc: "Clear, highly focused guidance optimized separately for 6–8 months and 9–12 months." },
                { title: "PRACTICAL", desc: "Real feeding situations, local foods, texture progression maps, and answers to common parent worries." },
                { title: "SIMPLE", desc: "No complicated medical or nutrition jargon. Just clear next steps and real-world guidelines." }
              ].map((diff, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#2D0833]/80 border border-[#5C0F68]/30 p-5 rounded-xl shadow-md relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[#689B3D]/5 rounded-full blur-xl"></div>
                  <h3 className="font-extrabold text-sm sm:text-base text-white tracking-widest uppercase mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#689B3D]" /> {diff.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#C1BEB7] leading-relaxed">{diff.desc}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* 11. SOCIAL PROOF */}
        <section className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            
            <div className="space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">PARENT TESTIMONIALS</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Parents want confidence — not more conflicting advice.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              {CONFIG.TESTIMONIALS.map((t, idx) => (
                <div 
                  key={idx} 
                  className="bg-[#2D0833]/80 border border-[#5C0F68]/30 p-5 rounded-xl flex flex-col justify-between shadow-lg relative"
                >
                  <span className="absolute top-4 right-4 text-3xl font-black text-[#5C0F68]/30">“</span>
                  <p className="text-xs sm:text-sm text-white/95 leading-relaxed italic z-10 mb-4">
                    "{t.quote}"
                  </p>
                  <div className="border-t border-[#5C0F68]/20 pt-3">
                    <h4 className="font-extrabold text-sm text-white">{t.name}</h4>
                    <p className="text-[10px] text-[#C1BEB7] font-bold tracking-wide uppercase">{t.baby_age}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* 12. WHAT YOU GET SUMMARY */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#2D0833] via-[#300b3a] to-[#2D0833]">
          <div className="max-w-3xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">INCLUDED TODAY</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Your masterclass includes
              </h2>
            </div>

            <div className="bg-[#2D0833]/80 border border-[#5C0F68]/30 rounded-2xl p-6 sm:p-8 shadow-xl">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                {[
                  "2 live pediatrician-led sessions",
                  `${CONFIG.DATE_1}: 6–8 months details`,
                  `${CONFIG.DATE_2}: 9–12 months details`,
                  "Practical safe food guidelines",
                  "Texture progression parameters",
                  "Balanced meal-building formulas",
                  "Allergen introduction guidance",
                  "Choking safety awareness guide",
                  "Family-food transition parameters",
                  "Answers to common parent worries"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-white/95">
                    <Check className="w-4 h-4 text-[#689B3D] mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </section>

        {/* 13. VALUE COMPARISON VISUAL */}
        <section className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-4xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">COMPARE THE CHOICE</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Instead of figuring it out one meal at a time...
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
              
              {/* LEFT: RANDOM ADVICE */}
              <div className="bg-[#2D0833] border border-[#5C0F68]/30 rounded-2xl p-6 shadow-lg text-left flex flex-col justify-between">
                <div className="space-y-4">
                  <span className="bg-[#5C0F68]/30 border border-[#5C0F68]/50 text-white text-[10px] font-extrabold tracking-widest uppercase px-2.5 py-1 rounded">
                    RANDOM ADVICE
                  </span>
                  <ul className="space-y-3 text-xs sm:text-sm text-[#C1BEB7] pt-2">
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500 shrink-0" /> Overwhelming Instagram reels
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500 shrink-0" /> Conflicting Google forums
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500 shrink-0" /> Outdated family advice
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500 shrink-0" /> 20 different feeding schedules
                    </li>
                    <li className="flex items-center gap-2">
                      <X className="w-4 h-4 text-red-500 shrink-0" /> Continuous feeding anxiety
                    </li>
                  </ul>
                </div>
                <div className="border-t border-[#5C0F68]/20 pt-4 mt-6 text-center">
                  <span className="text-red-400 font-extrabold text-base tracking-wide uppercase">CONFUSION</span>
                </div>
              </div>

              {/* RIGHT: CLEAR ROADMAP */}
              <div className="bg-[#2D0833] border-2 border-[#689B3D]/30 rounded-2xl p-6 shadow-xl text-left flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#689B3D]/5 rounded-full blur-xl"></div>
                <div className="space-y-4">
                  <span className="bg-[#689B3D] text-white text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded">
                    CLEAR ROADMAP
                  </span>
                  <ul className="space-y-3 text-xs sm:text-sm text-white/95 pt-2">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#689B3D] shrink-0" /> Pediatrician-backed milestones
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#689B3D] shrink-0" /> Clear 6–8 Month guidelines
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#689B3D] shrink-0" /> Structured 9–12 Month plan
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#689B3D] shrink-0" /> Safe texture & allergen guide
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#689B3D] shrink-0" /> Complete peace of mind
                    </li>
                  </ul>
                </div>
                <div className="border-t border-[#689B3D]/20 pt-4 mt-6 text-center">
                  <span className="text-[#689B3D] font-extrabold text-base tracking-wide uppercase">CONFIDENCE</span>
                </div>
              </div>

            </div>

          </div>
        </section>

        {/* 14. VALUE / OFFER SECTION */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#2D0833] via-[#3a0c47] to-[#2D0833]">
          <div className="max-w-3xl mx-auto text-center space-y-10">
            
            <div className="space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">SECURE YOUR SEAT</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Get the complete 6–12 month feeding roadmap
              </h2>
              <p className="text-sm sm:text-base text-[#C1BEB7] max-w-xl mx-auto leading-relaxed">
                Two focused live sessions designed to take you from starting solids to moving towards family foods successfully.
              </p>
            </div>

            {/* Offer Pricing Card */}
            <div className="bg-[#2D0833] border-2 border-[#5C0F68] rounded-2xl p-6 sm:p-8 max-w-md mx-auto shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#689B3D]/10 rounded-full blur-2xl"></div>
              
              <span className="text-[10px] font-black text-white bg-[#5C0F68] px-3 py-1 rounded-full uppercase tracking-wider inline-block mb-2">
                {CONFIG.OFFER_LABEL}
              </span>

              {/* Exact pricing visual hierarchy rule */}
              <div className="space-y-2 py-4">
                <p className="text-lg sm:text-xl text-white/50 line-through font-bold">{CONFIG.REGULAR_PRICE}</p>
                <h3 className="text-4xl sm:text-5xl font-black text-white tracking-tight">{CONFIG.OFFER_PRICE}</h3>
                <p className="text-xs sm:text-sm font-black text-[#689B3D] tracking-wide uppercase pt-1">
                  2 LIVE SESSIONS · 6–12 MONTHS
                </p>
                <p className="text-xs text-[#C1BEB7] font-semibold">{CONFIG.DATE_1} & {CONFIG.DATE_2} · {CONFIG.TIME}</p>
              </div>

              <div className="pt-2">
                <button 
                  data-testid="pricing-join-masterclass-button"
                  onClick={() => handleCtaClick("pricing")}
                  className="w-full bg-[#689B3D] text-white font-extrabold text-base py-4 px-6 rounded-xl hover:bg-[#689B3D]/90 transition duration-300 shadow-md flex items-center justify-center gap-2"
                >
                  JOIN THE MASTERCLASS <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              <p className="text-[10px] sm:text-xs text-[#C1BEB7] font-semibold mt-4">
                Live online · Simple English
              </p>
            </div>

          </div>
        </section>

        {/* 15. FAQ ACCORDION SECTION */}
        <section className="py-16 px-4 bg-[#2D0833] border-t border-b border-[#5C0F68]/20">
          <div className="max-w-3xl mx-auto space-y-12">
            
            <div className="text-center space-y-3">
              <span className="text-[#689B3D] text-xs font-black tracking-widest uppercase block">COMMON QUESTIONS</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                You may still be wondering...
              </h2>
            </div>

            {/* FAQ Accordion list */}
            <div className="space-y-4 text-left">
              {[
                {
                  q: "Is this suitable if my baby is just turning 6 months?",
                  a: "Yes! Session 1 is explicitly designed for babies starting solids or in the early stages of solid feeding. We will checklist safety signs and readiness so you begin on the exact right day."
                },
                {
                  q: "My baby is already 8–9 months. Is this still useful?",
                  a: "Absolutely. The structured roadmap covers Session 2 which is deeply focused on the critical 9-12 month transition: progressing textures, safe finger foods, self-feeding, and matching family meals."
                },
                {
                  q: "What if my baby is already eating solids?",
                  a: "Many babies eat solids but get stuck on smooth purees. This masterclass will show you safe texture milestones, balanced meal formulas, and how to safely transition to table textures by age one."
                },
                {
                  q: "Will you teach what foods to give?",
                  a: "Yes! We will guide you through age-safe food selections, balanced nutrition mixes, and iron-dense choices without confusing complex jargon."
                },
                {
                  q: "Will you teach finger foods?",
                  a: "Yes. Session 2 details safe self-feeding sizes, appropriate textures, and how to let your baby safely hold and eat food without worry."
                },
                {
                  q: "Will you cover gagging and choking?",
                  a: "Yes. Understanding the physical gag reflex is vital to stay calm. We will cover the differences, reflexes, safety rules, and pediatric choking precautions."
                },
                {
                  q: "Will you cover eggs and common allergens?",
                  a: "Yes. We cover the clinical protocol to safely introduce high-allergy foods one by one. This is educational guidance, not individual allergy management."
                },
                {
                  q: "Will you teach recipes?",
                  a: "We will teach you the underlying principles and meal layouts to easily compile nutritious local foods yourself, rather than just copying fixed ingredient lists."
                },
                {
                  q: "Will I get a recording?",
                  a: "Full live interactive participation is highly recommended. Recording access parameters will be guided dynamically according to TagMango platform options."
                },
                {
                  q: "Can I ask about my baby's specific problem?",
                  a: "General baby solid feeding questions can be asked. However, specific medical treatments, complex issues, poor growth, or diagnosis can only be assessed in an individual medical consultation."
                }
              ].map((item, idx) => {
                const isOpen = !!faqOpenState[idx];
                return (
                  <div 
                    key={idx} 
                    className="bg-[#2D0833] border border-[#5C0F68]/40 rounded-xl overflow-hidden transition-all duration-300"
                  >
                    <button 
                      data-testid={`faq-toggle-${idx}`}
                      onClick={() => toggleFaq(idx)}
                      className="w-full flex justify-between items-center p-5 text-left font-bold text-white hover:bg-[#5C0F68]/10 text-sm sm:text-base focus:outline-none transition-colors"
                    >
                      <span>{item.q}</span>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-[#689B3D] shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#689B3D] shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="p-5 pt-0 text-xs sm:text-sm text-[#C1BEB7] leading-relaxed border-t border-[#5C0F68]/10">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </section>

        {/* 16. FINAL CTA */}
        <section className="py-20 px-4 bg-gradient-to-b from-[#2D0833] to-[#1C0521] text-center relative overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#5C0F68]/20 rounded-full blur-[120px] pointer-events-none"></div>

          <div className="max-w-3xl mx-auto space-y-8 relative z-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
              Your baby's feeding journey <br className="hidden sm:inline" />
              doesn't have to be confusing.
            </h2>
            <p className="text-sm sm:text-base text-[#C1BEB7] max-w-xl mx-auto leading-relaxed">
              Know what to feed, how to progress and what comes next — from 6 to 12 months.
            </p>

            <div className="bg-[#2D0833]/90 border border-[#5C0F68] p-6 rounded-2xl max-w-md mx-auto space-y-4 shadow-2xl">
              <div className="flex justify-center gap-4 text-xs font-black uppercase text-[#689B3D] tracking-widest">
                <span>{CONFIG.DATE_1}</span>
                <span>•</span>
                <span>{CONFIG.DATE_2}</span>
              </div>
              <h3 className="font-extrabold text-white text-base">
                2 Live Sessions · {CONFIG.TIME}
              </h3>
              
              <div className="py-2 flex flex-wrap justify-center items-baseline gap-x-2">
                <span className="text-base sm:text-lg text-white/50 line-through font-bold">{CONFIG.REGULAR_PRICE}</span>
                <span className="text-3xl font-black text-white">{CONFIG.OFFER_PRICE}</span>
                <span className="text-[10px] text-white bg-[#689B3D] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                  2 LIVE SESSIONS
                </span>
              </div>

              <button 
                data-testid="final-join-masterclass-button"
                onClick={() => handleCtaClick("final")}
                className="w-full bg-[#689B3D] text-white font-extrabold text-base py-4 px-6 rounded-xl hover:bg-[#689B3D]/90 transition duration-300 shadow-md flex items-center justify-center gap-2"
              >
                JOIN THE MASTERCLASS <ArrowRight className="w-5 h-5" />
              </button>
              
              <p className="text-[10px] sm:text-xs text-[#C1BEB7] font-semibold">
                Live online · Simple English
              </p>
            </div>

          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-[#5C0F68]/30 bg-[#1C0521] py-12 px-4 text-[#C1BEB7] text-xs">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div data-testid="footer-brand-logo" className="flex items-center gap-3">
            <ConfidentParentingHubLogo className="w-8 h-8" />
            <div className="flex flex-col text-left">
              <span className="font-bold text-white">{CONFIG.BRAND_NAME}</span>
              <span className="text-[10px] tracking-wider uppercase">Dr. Anuradha H S</span>
            </div>
          </div>
          
          <div className="flex flex-col items-center md:items-end gap-2 text-center md:text-right">
            <p>© 2026 {CONFIG.BRAND_NAME}. All Rights Reserved.</p>
            <p>Support contact: <a href={`mailto:${CONFIG.CONTACT_EMAIL}`} className="underline text-[#689B3D]">{CONFIG.CONTACT_EMAIL}</a></p>
          </div>
        </div>
      </footer>

      {/* 14. STICKY MOBILE CTA */}
      {showStickyBar && (
        <div data-testid="sticky-cta-bar" className="sm:hidden fixed bottom-0 left-0 right-0 bg-[#2D0833]/95 backdrop-blur-md border-t border-[#5C0F68]/50 z-50 p-4 flex justify-between items-center gap-4 shadow-2xl animate-slide-up">
          <div className="text-left">
            <h4 className="font-bold text-xs text-white truncate leading-tight">Starting Solids Made Simple</h4>
            <p className="text-[11px] text-[#689B3D] font-extrabold mt-0.5">{CONFIG.OFFER_PRICE} · 30 + 31 Oct</p>
          </div>
          <button 
            data-testid="sticky-join-now-button"
            onClick={() => handleCtaClick("sticky")}
            className="bg-[#689B3D] text-white font-extrabold text-xs px-4 py-3 rounded-lg shadow-md active:scale-95 transition whitespace-nowrap"
          >
            JOIN NOW →
          </button>
        </div>
      )}

    </div>
  );
}
